import React, { useState, useRef, useEffect, useMemo } from 'react';
import { playRange, stopAll } from '../utils/audio';
import { splitToken } from '../utils/wordText';
import { API_BASE } from '../api';
import { useVersions, shownVersions } from '../hooks/useVersions';
import RequestPassage from './RequestPassage';

const API = API_BASE;

function getAudioUrl(sentence) {
  return sentence.audio ? `${API}/audio/${sentence.audio}` : null;
}

// 文 (whole story) — one card per PARAGRAPH (sentences flow as prose inside).
// Texts with chapter sections (Speaking Book) page ONE CHAPTER AT A TIME with a
// 目錄 selector; scene/TOC sections (Seediq Bale, Temi myths) stay a continuous
// scroll with a chip above each paragraph and a jump-目錄.
export default function ParagraphView({ text, activeSentId, onVisibleSentence, onPlayingWord, onWordClick, getWordStatus, focusSentId = null, focusWIdx = -1 }) {
  const [playingId, setPlayingId]     = useState(null);
  const [activeWordIdx, setActiveWordIdx] = useState(-1);
  const [showZh, setShowZh]           = useState({});
  const { sel } = useVersions();
  const activeSentRef = useRef(null);
  const paraTopRef    = useRef(null);
  const seqRef        = useRef(null);

  const paragraphs = useMemo(() => (text.paragraphs || []).filter(p => (p.sentences || []).length), [text.paragraphs]);
  const allSents = useMemo(() => paragraphs.flatMap(p => p.sentences), [paragraphs]);

  const sections   = useMemo(() => text.sections || [], [text.sections]);
  const scenesOnly = sections.length > 0 && sections.every(s => s.kind === 'scene');
  const secByStart = useMemo(() => {
    const m = {};
    sections.forEach(sec => { m[sec.startId] = sec; });
    return m;
  }, [sections]);

  // Chapters (non-scene sections): paragraph ranges, paged one at a time.
  const chapters = useMemo(() => {
    if (!sections.length || scenesOnly) return null;
    const at = sections.map(sec => paragraphs.findIndex(p => p.sentences.some(s => s.id === sec.startId)));
    return sections.map((sec, i) => ({
      sec,
      from: Math.max(0, at[i]),
      to: (i + 1 < sections.length && at[i + 1] >= 0 ? at[i + 1] : paragraphs.length) - 1,
    }));
  }, [sections, scenesOnly, paragraphs]);
  const [chapIdx, setChapIdx] = useState(0);

  // Land (and stay) on the chapter containing the active sentence.
  useEffect(() => {
    if (!chapters || !activeSentId) return;
    const pi = paragraphs.findIndex(p => p.sentences.some(s => s.id === activeSentId));
    if (pi < 0) return;
    const ci = chapters.findIndex(c => pi >= c.from && pi <= c.to);
    if (ci >= 0 && ci !== chapIdx) setChapIdx(ci);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSentId, chapters, paragraphs]);

  const shownParas = chapters
    ? paragraphs.slice(chapters[chapIdx].from, chapters[chapIdx].to + 1)
    : paragraphs;
  const chapterSents = useMemo(() => shownParas.flatMap(p => p.sentences), [shownParas]);

  useEffect(() => {
    // Landing at the top of a titled passage: bring the heading + first verse into view,
    // not the middle of the card (a whole pericope is one tall card, so 'center' would
    // scroll its title and first verse off the top). Mid-passage keeps center anchoring.
    if (paraTopRef.current)
      paraTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else if (activeSentRef.current)
      activeSentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeSentId]);

  // Report the sentence at the top of the viewport (view-switch anchoring).
  useEffect(() => {
    if (!onVisibleSentence) return;
    let raf = 0;
    const scan = () => {
      raf = 0;
      const els = document.querySelectorAll('.story-row[data-sid]');
      let best = null, bestTop = Infinity;
      els.forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.bottom > 110 && r.top < bestTop) { bestTop = r.top; best = el; }
      });
      if (best) onVisibleSentence(best.getAttribute('data-sid'));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(scan); };
    window.addEventListener('scroll', onScroll, { passive: true });
    scan();
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [onVisibleSentence, chapIdx]);

  useEffect(() => {
    if (playingId && onVisibleSentence) onVisibleSentence(playingId);
  }, [playingId, onVisibleSentence]);

  useEffect(() => {
    if (!onPlayingWord) return;
    if (!playingId || activeWordIdx < 0) { onPlayingWord(null); return; }
    onPlayingWord({ sentId: playingId, wIdx: activeWordIdx });
  }, [playingId, activeWordIdx, onPlayingWord]);
  useEffect(() => () => { if (onPlayingWord) onPlayingWord(null); }, [onPlayingWord]);

  function playSentenceItem(sentence, onDone) {
    const url = getAudioUrl(sentence);
    if (!url) { onDone?.(); return; }
    const segStart = sentence.audioStart ?? null;
    const segEnd   = sentence.audioEnd   ?? null;
    const words    = sentence.words || [];
    setPlayingId(sentence.id);
    setActiveWordIdx(-1);
    playRange(url, segStart, segEnd,
      (relTime) => {
        const idx = words.findIndex((w, i) =>
          relTime >= w.start && (i === words.length - 1 || relTime < words[i + 1].start));
        setActiveWordIdx(idx);
      },
      () => { setPlayingId(null); setActiveWordIdx(-1); onDone?.(); }
    );
  }

  function playFromSentence(sentences, startIdx) {
    seqRef.current = { sentences, idx: startIdx };
    function next() {
      const seq = seqRef.current;
      if (!seq || seq.idx >= seq.sentences.length) {
        setPlayingId(null); setActiveWordIdx(-1); seqRef.current = null; return;
      }
      playSentenceItem(seq.sentences[seq.idx], () => {
        if (seqRef.current) { seqRef.current.idx++; next(); }
      });
    }
    next();
  }

  // ▶ on a card: continuous play from HERE onward (to the chapter's end when
  // paged, to the story's end otherwise); tapping the playing card's ❚❚ stops.
  function handleRowPlay(sentList, e) {
    e.stopPropagation();
    if (sentList.some(s => s.id === playingId)) {
      stopAll(); seqRef.current = null; setPlayingId(null); return;
    }
    stopAll(); seqRef.current = null;
    const pool = chapters ? chapterSents : allSents;
    const i = pool.findIndex(s => s.id === sentList[0].id);
    playFromSentence(pool, Math.max(0, i));
  }

  function handleWordClick(word, wordIdx, sentence, e) {
    e.stopPropagation();
    const url      = getAudioUrl(sentence);
    const segStart = sentence.audioStart ?? null;
    const segEnd   = sentence.audioEnd   ?? null;
    if (e.shiftKey) {   // play from here to the sentence end; no word-view
      if (url) {
        const absStart = (segStart ?? 0) + word.start;
        const words   = sentence.words || [];
        const sentEnd = segEnd ?? ((segStart ?? 0) + words[words.length - 1]?.end + 0.1);
        setPlayingId(sentence.id);
        setActiveWordIdx(wordIdx);
        playRange(url, absStart, sentEnd,
          (relTime) => {
            const local = relTime + word.start;
            const idx = words.findIndex((w, i) =>
              local >= w.start && (i === words.length - 1 || local < words[i + 1].start));
            setActiveWordIdx(idx);
          },
          () => { setPlayingId(null); setActiveWordIdx(-1); }
        );
      }
      return;
    }
    onWordClick(word, wordIdx, sentence.id, url, segStart, e);
  }

  function renderWords(sentence) {
    const partPlaying = playingId === sentence.id;
    return (sentence.words || []).map((word, wi) => {
      const { lead, core, trail } = splitToken(word.text);
      return (
        <React.Fragment key={`${sentence.id}-${wi}`}>
          {lead && <span className="word-punct">{lead}</span>}
          {core && (
            <span
              className={`word status-${getWordStatus(core)}${partPlaying && wi === activeWordIdx ? ' word-active' : ''}${sentence.id === focusSentId && wi === focusWIdx ? ' wordview-focus' : ''}`}
              onClick={(e) => handleWordClick({ ...word, text: core }, wi, sentence, e)}
            >{core}</span>
          )}
          {trail && <span className="word-punct">{trail}</span>}
          {' '}
        </React.Fragment>
      );
    });
  }

  // One card over a run of sentences (normally a whole paragraph).
  function renderCard(sentList, headSec = null) {
    const isActive  = sentList.some(s => s.id === activeSentId);
    const isPlaying = sentList.some(s => s.id === playingId);
    const key       = sentList[0].id;
    const zhOn      = !!showZh[key];
    // 文 runs the FIRST parallel text the reader chose (same resolution as 句/段, so the
    // two views never disagree about which translation is on screen).
    const zh = sentList.map(s => {
      if (!s.versions) return s.translation || '';
      const v = shownVersions(sel, text.dialect, s.versions);
      return v.length ? v[0].text : '';
    }).join('');
    const anchorId  = sentList.find(s => s.id === playingId)?.id
                   ?? sentList.find(s => s.id === activeSentId)?.id
                   ?? key;
    return (
      <div key={key} data-sid={anchorId} className={`story-row${headSec ? ' story-row-head' : ''}`}
        ref={isActive ? activeSentRef : null}>
        {/* A passage awaiting TTS has no clip to play — show the reason, not a dead button. */}
        {sentList.some(s => s.audio) ? (
          <button className="story-play" onClick={(e) => handleRowPlay(sentList, e)}>
            {isPlaying ? '❚❚' : '▶'}
          </button>
        ) : (
          <span className="story-noaudio" title="語音待補">待補</span>
        )}
        <div className="story-body">
          <div className="story-line">
            {headSec && <span className="ssh-n">{headSec.n}</span>}
            {sentList.map(renderWords)}
          </div>
          {headSec && headSec.zhTitle && <div className="ssh-zh">{headSec.zhTitle}</div>}
          {!headSec && zh && (
            <div className="sv-cnwrap">
              <button className="sv-cnlink" onClick={(e) => { e.stopPropagation(); setShowZh(p => ({ ...p, [key]: !p[key] })); }}>
                {zhOn ? '隱藏中文' : '顯示中文'}
              </button>
              {zhOn && <p className="sv-trans">{zh}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderParagraph(p) {
    const sents = p.sentences;
    const sec = secByStart[sents[0].id];
    const isHead = sec && sec.spoken;
    const isStaticHead = sec && !sec.kind && !sec.spoken;
    // A titled passage whose first verse is the active one: anchor scroll to the
    // paragraph top so the heading shows (see the scroll effect above).
    const atTitledStart = isStaticHead && sents[0].id === activeSentId;
    return (
      <div key={p.id} className="story-para">
        {sec && sec.kind === 'scene' && (
          <div className="story-scene-chip">
            {sec.n}{sec.speakers ? ` · ${sec.speakers}` : ''}{sec.desc ? ` — ${sec.desc}` : ''}
          </div>
        )}
        {isStaticHead && (
          <div className="story-section-head" data-sid={sec.startId} ref={atTitledStart ? paraTopRef : null}>
            {/* Truku Bible: the section-start verse carries a native pericope title —
                lead with it (Truku-first), then 中文, then English. Other texts have
                no per-verse heading, so they fall back to the English section title. */}
            <div className="ssh-title"><span className="ssh-n">{sec.n}</span>{sents[0].heading || sec.title}</div>
            {(sents[0].headingZh || sec.zhTitle) && <div className="ssh-zh">{sents[0].headingZh || sec.zhTitle}</div>}
            {sents[0].heading && sec.title && <div className="ssh-en">{sec.title}</div>}
          </div>
        )}
        {isHead
          ? <>{renderCard([sents[0]], sec)}{sents.length > 1 && renderCard(sents.slice(1))}</>
          : renderCard(sents)}
      </div>
    );
  }

  const chapLabel = (c) => `${c.sec.n} ${c.sec.zhTitle || c.sec.title || ''}`.trim();

  function jumpToScene(e) {
    const sid = e.target.value;
    if (!sid) return;
    e.target.value = '';
    document.querySelector(`.story-view [data-sid="${sid}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="story-view">
      {chapters && (
        <div className="story-toc">
          <button className="sv-navbtn" disabled={chapIdx === 0}
            onClick={() => { stopAll(); setChapIdx(chapIdx - 1); window.scrollTo(0, 0); }}>‹</button>
          <select value={chapIdx} onChange={(e) => { stopAll(); setChapIdx(+e.target.value); window.scrollTo(0, 0); }}>
            {chapters.map((c, i) => <option key={i} value={i}>{chapLabel(c)}</option>)}
          </select>
          <button className="sv-navbtn" disabled={chapIdx === chapters.length - 1}
            onClick={() => { stopAll(); setChapIdx(chapIdx + 1); window.scrollTo(0, 0); }}>›</button>
        </div>
      )}
      {!chapters && sections.length > 0 && (
        <div className="story-toc">
          <select defaultValue="" onChange={jumpToScene}>
            <option value="" disabled>目錄…</option>
            {sections.map(sec => (
              <option key={sec.startId} value={sec.startId}>
                {sec.n}{sec.speakers ? ` · ${sec.speakers}` : ''}{sec.desc ? ` ${sec.desc}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      {shownParas.map(renderParagraph)}
      {chapters && (
        <div className="story-chapnav">
          <button className="sv-navbtn" disabled={chapIdx === 0}
            onClick={() => { stopAll(); setChapIdx(chapIdx - 1); window.scrollTo(0, 0); }}>‹ 上一章</button>
          <span className="sv-navpos">{chapIdx + 1} / {chapters.length}</span>
          <button className="sv-navbtn" disabled={chapIdx === chapters.length - 1}
            onClick={() => { stopAll(); setChapIdx(chapIdx + 1); window.scrollTo(0, 0); }}>下一章 ›</button>
        </div>
      )}
      {text.requests && <RequestPassage textTitle={text.titleTranslation || text.title} />}
      <div style={{ height: 60 }} />
    </div>
  );
}
